import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, IndianRupee, Calendar, CheckCircle, XCircle, MapPin, Upload, QrCode, MessageCircle, Award, TrendingUp, User, Phone, MapPinned, Clock, History, FileText, Camera, Download, BookOpen, Lock } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCORE_PARAMS = [
  { key: "personality", label: "Appearance", icon: "✨" },
  { key: "communication", label: "Communication", icon: "💬" },
  { key: "punctuality", label: "Punctuality", icon: "⏰" },
  { key: "eager_to_learn", label: "Eager to Learn", icon: "📚" },
  { key: "work_results", label: "Work Results", icon: "⭐" }
];

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentTerms, setShowPaymentTerms] = useState(false);
  const [showDailyScores, setShowDailyScores] = useState(false);
  const [showParameterScores, setShowParameterScores] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showCourseRequest, setShowCourseRequest] = useState(false);
  const [showProfilePhoto, setShowProfilePhoto] = useState(false);
  const [dailyScores, setDailyScores] = useState([]);
  const [locationStatus, setLocationStatus] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("scanner");
  const [selectedFile, setSelectedFile] = useState(null);
  const [courseRequestForm, setCourseRequestForm] = useState({ action: "add", course_id: "", reason: "" });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [batchSchedules, setBatchSchedules] = useState({});
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  
  // Daily Tasks States
  const [dailyTasks, setDailyTasks] = useState([]);
  const [taskDialog, setTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskFiles, setTaskFiles] = useState([]);
  
  // Password Change States
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [selectedCourseSchedule, setSelectedCourseSchedule] = useState([]);
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [todayClasses, setTodayClasses] = useState([]);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  // Practice Kit States
  const [kits, setKits] = useState([]);
  const [kitDialog, setKitDialog] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);

  useEffect(() => {
    const studentData = localStorage.getItem("student");
    if (!studentData) {
      navigate("/");
      return;
    }
    
    const parsedStudent = JSON.parse(studentData);
    fetchStudentDetails(parsedStudent.id);
  }, []);

  const fetchStudentDetails = async (studentId) => {
    try {
      const [studentRes, booksRes, scheduleRes, todayClassesRes] = await Promise.all([
        axios.get(`${API}/student/${studentId}`),
        axios.get(`${API}/student/${studentId}/books`),
        axios.get(`${API}/student/${studentId}/schedule`),
        axios.get(`${API}/student/${studentId}/today-classes`)
      ]);
      setStudent(studentRes.data.student);
      setBooks(booksRes.data.books);
      setBatchSchedules(scheduleRes.data.schedules);
      setEnrolledCourses(scheduleRes.data.enrolled_courses);
      setTodayClasses(todayClassesRes.data.classes);
      localStorage.setItem("student", JSON.stringify(studentRes.data.student));
      
      // Fetch all incomplete daily tasks
      const tasksRes = await axios.get(`${API}/student/tasks/${studentId}`);
      setDailyTasks(tasksRes.data.tasks || []);
      
      // Fetch practice kits
      const kitsRes = await axios.get(`${API}/student/${studentId}/kits`);
      setKits(kitsRes.data.kits || []);
    } catch (error) {
      toast.error("Failed to fetch student details");
    } finally {
      setLoading(false);
    }
  };

  const downloadBook = (book) => {
    const link = document.createElement('a');
    link.href = book.file_base64;
    link.download = `${book.title}.pdf`;
    link.click();
  };

  const fetchBatchSchedule = async () => {
    try {
      const studentData = JSON.parse(localStorage.getItem("student"));
      const response = await axios.get(`${API}/student/${studentData.id}/schedule`);
      setBatchSchedules(response.data.schedules);
      setEnrolledCourses(response.data.enrolled_courses);
    } catch (error) {
      console.error("Failed to fetch schedule");
    }
  };

  const fetchTodayClasses = async () => {
    try {
      const studentData = JSON.parse(localStorage.getItem("student"));
      const response = await axios.get(`${API}/student/${studentData.id}/today-classes`);
      setTodayClasses(response.data.classes);
    } catch (error) {
      console.error("Failed to fetch today's classes");
    }
  };

  const markClassAttendance = async (classItem) => {
    try {
      const studentData = JSON.parse(localStorage.getItem("student"));
      const today = new Date().toISOString().split('T')[0];
      
      await axios.post(`${API}/student/mark-class-attendance`, {
        student_id: studentData.id,
        schedule_id: classItem.id,
        date: today,
        course_type: classItem.course_type
      });
      
      toast.success("Attendance marked successfully!");
      fetchTodayClasses(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const openCourseSchedule = (courseName) => {
    const schedules = batchSchedules[courseName.toLowerCase()] || [];
    setSelectedCourseSchedule(schedules);
    setSelectedCourseName(courseName);
    setScheduleDialog(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("student");
    navigate("/");
  };

  const openPaymentDialog = () => {
    setShowPaymentTerms(true);
  };

  const proceedToPayment = () => {
    setShowPaymentTerms(false);
    setPaymentDialog(true);
    setPaymentAmount("");
    setPaymentMethod("scanner");
    setSelectedFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setSelectedFile(file);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    if (paymentMethod !== "cash" && !selectedFile) {
      toast.error("Please select a payment screenshot");
      return;
    }

    try {
      let base64 = null;
      if (selectedFile) {
        base64 = await convertToBase64(selectedFile);
      }

      await axios.post(`${API}/payment/submit`, {
        student_id: student.id,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        screenshot_base64: base64
      });
      
      toast.success("Payment submitted successfully!");
      setPaymentDialog(false);
      fetchStudentDetails(student.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/student/${student.id}/payments`);
      setPaymentHistory(response.data.payments);
      setPaymentHistoryDialog(true);
    } catch (error) {
      toast.error("Failed to fetch payment history");
    }
  };

  const markAttendance = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLocationStatus("Getting your location...");
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.post(`${API}/attendance/mark`, {
            user_id: student.id,
            user_type: "student",
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            action: "time_in"
          });
          
          toast.success(response.data.message);
          setLocationStatus("");
          fetchStudentDetails(student.id);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to mark attendance");
          setLocationStatus("");
        }
      },
      (error) => {
        toast.error("Please enable location access to mark attendance");
        setLocationStatus("");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const openWhatsApp = (number) => {
    window.open(`https://wa.me/${number.replace(/[^0-9]/g, '')}`, '_blank');
  };

  const fetchDailyScores = async () => {
    try {
      const response = await axios.get(`${API}/student/${student.id}/scores`);
      setDailyScores(response.data.scores);
    } catch (error) {
      toast.error("Failed to fetch daily scores");
    }
  };

  const fetchDailyTasks = async () => {
    try {
      const response = await axios.get(`${API}/student/tasks/${student.id}`);
      setDailyTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Failed to fetch daily tasks:", error);
    }
  };

  const handleTaskSubmit = async () => {
    if (taskFiles.length === 0) {
      toast.error("Please select at least one image or video");
      return;
    }

    try {
      // Convert files to base64 and compress
      const convertedFiles = await Promise.all(
        taskFiles.map(async (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      await axios.post(`${API}/student/task/submit`, {
        task_id: selectedTask.id,
        student_id: student.id,
        submission_files: convertedFiles
      });

      toast.success("Task submitted successfully!");
      setTaskDialog(false);
      setTaskFiles([]);
      fetchDailyTasks(); // Refresh tasks
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit task");
    }
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New password and confirmation don't match");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }

    if (passwordForm.newPassword === passwordForm.currentPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    try {
      await axios.put(`${API}/student/change-password`, {
        student_id: student.id,
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      toast.success("Password changed successfully!");
      setPasswordDialog(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const openParameterScores = (param) => {
    setSelectedParameter(param);
    setShowParameterScores(true);
  };

  const getAttendanceStatus = (date, schedules) => {
    if (!student?.batch_start_date) return null;
    const startDate = new Date(student.batch_start_date);
    const checkDate = new Date(date);
    if (checkDate < startDate) return null;
    
    const attended = student.attendance_records?.some(record => 
      record.date.startsWith(date)
    );
    return attended ? "Present" : "Absent";
  };

  const handleCourseRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/student/course-request`, {
        student_id: student.id,
        ...courseRequestForm
      });
      toast.success("Course request submitted to admin");
      setShowCourseRequest(false);
      setCourseRequestForm({ action: "add", course_id: "", reason: "" });
    } catch (error) {
      toast.error("Failed to submit request");
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      const base64 = await convertToBase64(file);
      
      try {
        await axios.put(`${API}/admin/student/${student.id}`, {
          profile_photo: base64
        });
        toast.success("Profile photo updated!");
        fetchStudentDetails(student.id);
        setShowProfilePhoto(false);
      } catch (error) {
        toast.error("Failed to update photo");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Account Inactive Warning */}
        {student && !student.is_active && (
          <Card className="mb-6 border-2 border-orange-500 bg-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-orange-900">Account Pending Activation</h3>
                  <p className="text-orange-800">Your account has been registered successfully. All features will be available after admin activation on Day 1 of class. You can view your details below but cannot perform any actions yet.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Header with Profile Photo */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Welcome, {student?.name}!</h1>
            <p className="text-gray-600 mt-1">Student Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Profile Photo */}
            <div className="relative">
              {student?.profile_photo ? (
                <img 
                  src={student.profile_photo} 
                  alt="Profile" 
                  className="w-[50px] h-[50px] rounded-full object-cover border-2 border-pink-500 cursor-pointer"
                  onClick={() => setShowProfilePhoto(true)}
                  data-testid="profile-photo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 24 24' fill='none' stroke='%23EC4899' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'%3E%3C/path%3E%3Ccircle cx='12' cy='7' r='4'%3E%3C/circle%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <div className="w-[50px] h-[50px] rounded-full bg-pink-100 border-2 border-pink-500 flex items-center justify-center cursor-pointer" onClick={() => setShowProfilePhoto(true)}>
                  <User className="w-6 h-6 text-pink-600" />
                </div>
              )}
            </div>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Batch and Profile Info */}
        <Card className="mb-6 border-2 border-pink-200 bg-white/80 backdrop-blur-sm" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Batch Number</p>
                <p className="font-semibold text-gray-900">{student?.batch_number || "Not Assigned"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Batch Start Date</p>
                <p className="font-semibold text-gray-900">
                  {student?.batch_start_date ? new Date(student.batch_start_date).toLocaleDateString() : "TBA"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Mobile</p>
                <p className="font-semibold text-gray-900 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {student?.mobile}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Your Mentor</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1 gap-2"
                  onClick={() => openWhatsApp(student?.mentor_whatsapp)}
                  data-testid="mentor-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4" />
                  {student?.mentor_name}
                </Button>
              </div>
            </div>
            
            {/* Password Change Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Account Security</p>
                  <p className="text-xs text-gray-600">Change your login password</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPasswordDialog(true)}
                  className="bg-blue-50 border-blue-300 hover:bg-blue-100"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Course Details */}
        <Card className="mb-6 border-2 border-purple-200 bg-white/80 backdrop-blur-sm" data-testid="course-details-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Course Details
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setShowCourseRequest(true)} data-testid="request-course-button">
                <FileText className="w-4 h-4 mr-2" />
                Request Course Change
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Course Name</p>
                <p className="font-semibold text-gray-900">{student?.course_name}</p>
                {student?.course_id === "course3" && student?.course_specialization && (
                  <p className="text-xs text-purple-600 font-medium mt-1">
                    {student.course_specialization}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-semibold text-gray-900">{student?.course_duration}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Fees</p>
                <p className="font-semibold text-gray-900 flex items-center gap-1">
                  <IndianRupee className="w-4 h-4" />
                  {student?.course_fees?.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Payment Status */}
          <Card className="border-2 border-purple-200 bg-white/80 backdrop-blur-sm" data-testid="payment-status-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5" />
                  Payment Status
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchPaymentHistory} data-testid="payment-history-button">
                  <History className="w-4 h-4 mr-2" />
                  Payment History
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Paid Fees</p>
                  <p className="text-2xl font-bold text-green-600">₹{student?.paid_fees?.toLocaleString() || 0}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Balance Fees</p>
                  <p className="text-2xl font-bold text-red-600">₹{student?.balance_fees?.toLocaleString() || 0}</p>
                </div>
              </div>
              
              {student?.balance_fees > 0 && (
                <Button
                  onClick={openPaymentDialog}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600"
                  data-testid="make-payment-button"
                >
                  <IndianRupee className="w-4 h-4 mr-2" />
                  Make Payment
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Today's Classes */}
          <Card className="border-2 border-green-200 bg-white/80 backdrop-blur-sm" data-testid="today-classes-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Today's Classes
              </CardTitle>
              <CardDescription>
                Mark attendance for each class
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {todayClasses.length > 0 ? (
                todayClasses.map((classItem, idx) => (
                  <Card key={idx} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">
                              {classItem.course_type === "makeup" && "💄"}
                              {classItem.course_type === "hair" && "💇"}
                              {classItem.course_type === "nails" && "💅"}
                              {classItem.course_type === "draping" && "👗"}
                            </span>
                            <span className="font-semibold capitalize">{classItem.course_type}</span>
                          </div>
                          <p className="text-sm text-gray-600">{classItem.topic}</p>
                        </div>
                        {classItem.attendance_marked ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-semibold">Present</span>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => markClassAttendance(classItem)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark Present
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No classes scheduled for today</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Class Schedule - Multiple Courses */}
        {enrolledCourses && enrolledCourses.length > 0 && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Class Schedule - Batch {student?.batch_number || "Not Assigned"}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {enrolledCourses.includes("makeup") && (
              <Card className="border-2 border-pink-200 hover:shadow-lg transition">
                <CardHeader className="bg-gradient-to-r from-pink-50 to-pink-100">
                  <CardTitle className="flex items-center justify-between">
                    <span>💄 Makeup Classes</span>
                    <Calendar className="w-5 h-5 text-pink-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">
                    {batchSchedules.makeup?.length || 0} scheduled classes
                  </p>
                  <Button 
                    onClick={() => {
                      fetchBatchSchedule().then(() => openCourseSchedule("Makeup"));
                    }}
                    className="w-full bg-gradient-to-r from-pink-500 to-pink-600"
                  >
                    View Schedule
                  </Button>
                </CardContent>
              </Card>
            )}

            {enrolledCourses.includes("hair") && (
              <Card className="border-2 border-purple-200 hover:shadow-lg transition">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
                  <CardTitle className="flex items-center justify-between">
                    <span>💇 Hair Classes</span>
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">
                    {batchSchedules.hair?.length || 0} scheduled classes
                  </p>
                  <Button 
                    onClick={() => {
                      fetchBatchSchedule().then(() => openCourseSchedule("Hair"));
                    }}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600"
                  >
                    View Schedule
                  </Button>
                </CardContent>
              </Card>
            )}

            {enrolledCourses.includes("nails") && (
              <Card className="border-2 border-blue-200 hover:shadow-lg transition">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
                  <CardTitle className="flex items-center justify-between">
                    <span>💅 Nails Classes</span>
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">
                    {batchSchedules.nails?.length || 0} scheduled classes
                  </p>
                  <Button 
                    onClick={() => {
                      fetchBatchSchedule().then(() => openCourseSchedule("Nails"));
                    }}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
                  >
                    View Schedule
                  </Button>
                </CardContent>
              </Card>
            )}

            {enrolledCourses.includes("draping") && (
              <Card className="border-2 border-green-200 hover:shadow-lg transition">
                <CardHeader className="bg-gradient-to-r from-green-50 to-green-100">
                  <CardTitle className="flex items-center justify-between">
                    <span>👗 Draping Classes</span>
                    <Calendar className="w-5 h-5 text-green-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">
                    {batchSchedules.draping?.length || 0} scheduled classes
                  </p>
                  <Button 
                    onClick={() => {
                      fetchBatchSchedule().then(() => openCourseSchedule("Draping"));
                    }}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600"
                  >
                    View Schedule
                  </Button>
                </CardContent>
              </Card>
              )}
            </div>
          </div>
        )}

        {/* Books and Notes */}
        <Card className="mb-6 border-2 border-indigo-200 bg-white/80 backdrop-blur-sm" data-testid="books-notes-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Books & Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {books.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {books.map((book, idx) => (
                  <Card key={idx} className="border hover:shadow-md transition">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{book.title}</p>
                          <p className="text-sm text-gray-600 capitalize">{book.file_type}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => downloadBook(book)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`download-book-${idx}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No books or notes available yet</p>
            )}
          </CardContent>
        </Card>

        {/* Daily Tasks */}
        <Card className="mb-6 border-2 border-orange-200 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Today's Home Tasks
            </CardTitle>
            <CardDescription>Complete and submit your daily practice tasks</CardDescription>
          </CardHeader>
          <CardContent>
            {dailyTasks.length > 0 ? (
              <div className="space-y-4">
                {dailyTasks.map((task, idx) => (
                  <Card key={idx} className="border-l-4 border-l-orange-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-semibold capitalize">
                              {task.course_type}
                            </span>
                            {task.is_submitted && (
                              <span className={`px-2 py-1 text-xs rounded-full font-semibold flex items-center gap-1 ${
                                task.submission_status === 'approved' 
                                  ? 'bg-green-100 text-green-700'
                                  : task.submission_status === 'rejected'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {task.submission_status === 'approved' && <CheckCircle className="w-3 h-3" />}
                                {task.submission_status === 'rejected' && <XCircle className="w-3 h-3" />}
                                {task.submission_status === 'pending' && <Clock className="w-3 h-3" />}
                                {task.submission_status === 'approved' ? 'Approved' 
                                 : task.submission_status === 'rejected' ? 'Rejected'
                                 : 'Pending Review'} ({task.submission_count} file{task.submission_count > 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{task.task_description}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>By: {task.teacher_name}</span>
                            <span className="text-red-600 font-semibold">
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        onClick={() => {
                          setSelectedTask(task);
                          setTaskDialog(true);
                        }}
                        size="sm"
                        variant={task.is_submitted ? "outline" : "default"}
                        className={!task.is_submitted ? "bg-orange-500 hover:bg-orange-600" : ""}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {task.is_submitted ? "Update Submission" : "Submit Task"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">All tasks completed! Great work! 🎉</p>
            )}
          </CardContent>
        </Card>

        {/* Practice Kits */}
        <Card className="mb-6 border-2 border-purple-200 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-500" />
              Practice Kits
            </CardTitle>
            <CardDescription>View your issued practice kits and their status</CardDescription>
          </CardHeader>
          <CardContent>
            {kits.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {kits.map((kit, idx) => (
                  <Card 
                    key={idx} 
                    className={`border-l-4 cursor-pointer hover:shadow-md transition ${
                      kit.is_returned ? 'border-l-green-500 bg-green-50/50' : 'border-l-purple-500'
                    }`}
                    onClick={() => {
                      setSelectedKit(kit);
                      setKitDialog(true);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {kit.kit_image_base64 && (
                          <img 
                            src={kit.kit_image_base64} 
                            alt={kit.kit_name}
                            className="w-16 h-16 object-cover rounded border"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-1">{kit.kit_name}</p>
                          <div className="space-y-1 text-xs text-gray-600">
                            <p>Issued by: {kit.issued_by_name}</p>
                            <p>Issue Date: {new Date(kit.issue_date).toLocaleDateString()}</p>
                            {kit.is_returned && kit.return_date && (
                              <p className="text-green-600 font-semibold">
                                ✓ Returned: {new Date(kit.return_date).toLocaleDateString()}
                              </p>
                            )}
                            {!kit.is_returned && (
                              <p className="text-purple-600 font-semibold">
                                📦 Currently with you
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No practice kits issued yet</p>
            )}
          </CardContent>
        </Card>

        {/* Ranking & Scores */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card className="border-2 border-amber-200 bg-white/80 backdrop-blur-sm" data-testid="rank-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Your Rank
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg">
                <p className="text-5xl font-bold text-amber-600 mb-2">
                  #{student?.rank || "-"}
                </p>
                <p className="text-gray-600 mb-1">Current Rank</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {student?.total_score || 0} marks
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200 bg-white/80 backdrop-blur-sm" data-testid="scores-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Your Daily Marks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {SCORE_PARAMS.map(param => {
                  const paramScores = dailyScores.map(s => s.scores[param.key] || 0);
                  const totalScore = paramScores.reduce((a, b) => a + b, 0);
                  const totalPossible = paramScores.length * 10;
                  
                  return (
                    <Card 
                      key={param.key}
                      className="border-2 hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg"
                      onClick={() => {
                        fetchDailyScores();
                        openParameterScores(param);
                      }}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">{param.icon}</div>
                        <p className="text-xs text-gray-600 mb-2">{param.label}</p>
                        <p className="text-xl font-bold text-blue-600">
                          {totalScore}/{totalPossible}
                        </p>
                        <p className="text-xs text-gray-500">Total Marks</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Terms Dialog */}
      <Dialog open={showPaymentTerms} onOpenChange={setShowPaymentTerms}>
        <DialogContent className="max-w-4xl max-h-[80vh]" data-testid="payment-terms-dialog">
          <DialogHeader>
            <DialogTitle>Payment Terms & Conditions</DialogTitle>
            <DialogDescription>Please read before making payment</DialogDescription>
          </DialogHeader>
          <div className="h-[500px] overflow-y-auto border rounded p-6 bg-gray-50 text-sm">
            <p className="text-gray-700">Payment terms content here...</p>
          </div>
          <Button onClick={proceedToPayment} className="w-full" data-testid="proceed-payment-button">
            I Agree, Proceed to Payment
          </Button>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Make Payment</DialogTitle>
            <DialogDescription>Enter payment details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                data-testid="payment-amount-input"
              />
              <p className="text-sm text-gray-500 mt-1">Balance: ₹{student?.balance_fees?.toLocaleString()}</p>
            </div>

            <div>
              <Label htmlFor="method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="scanner">Scanner (QR Code)</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentMethod === "scanner" && (
              <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg">
                <QrCode className="w-6 h-6 text-gray-600 mb-3" />
                <img 
                  src="/payment-qr.jpg" 
                  alt="Payment QR Code" 
                  className="max-w-xs w-full h-auto border-4 border-white shadow-lg rounded-lg"
                  data-testid="payment-qr-image"
                />
              </div>
            )}

            {paymentMethod !== "cash" && (
              <div>
                <Label>Upload Payment Screenshot</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="payment-file-input"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="select-screenshot-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {selectedFile ? selectedFile.name : "Select Screenshot"}
                </Button>
              </div>
            )}

            <Button
              onClick={handlePaymentSubmit}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600"
              data-testid="submit-payment-button"
            >
              Submit Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryDialog} onOpenChange={setPaymentHistoryDialog}>
        <DialogContent className="max-w-3xl" data-testid="payment-history-dialog">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
            <DialogDescription>All your payment transactions</DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Payment Type</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-3">{new Date(payment.date).toLocaleDateString()}</td>
                    <td className="p-3">₹{payment.amount.toLocaleString()}</td>
                    <td className="p-3 capitalize">{payment.payment_method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {paymentHistory.length === 0 && (
              <p className="text-center py-8 text-gray-500">No payment history yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Daily Scores Dialog */}
      <Dialog open={showDailyScores} onOpenChange={setShowDailyScores}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="daily-scores-dialog">
          <DialogHeader>
            <DialogTitle>Daily Marks History</DialogTitle>
            <DialogDescription>Your performance marks for each day</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {dailyScores.length > 0 ? (
              dailyScores.map((score, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg">{new Date(score.date).toLocaleDateString()}</CardTitle>
                      <span className="text-2xl font-bold text-purple-600">{score.total}/50</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-4">
                      {SCORE_PARAMS.map(param => (
                        <div key={param.key} className="text-center">
                          <p className="text-xs text-gray-600 mb-1">{param.label}</p>
                          <p className="text-xl font-bold text-gray-900">{score.scores[param.key]}/10</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                No marks recorded yet
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Parameter Scores Dialog */}
      <Dialog open={showParameterScores} onOpenChange={setShowParameterScores}>
        <DialogContent className="max-w-2xl" data-testid="parameter-scores-dialog">
          <DialogHeader>
            <DialogTitle>{selectedParameter?.label} - Daily Marks</DialogTitle>
            <DialogDescription>Your marks for this parameter each day</DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[500px] overflow-y-auto space-y-2">
            {dailyScores.map((score, idx) => (
              <div key={idx} className="flex justify-between p-3 bg-gray-50 rounded">
                <span>{new Date(score.date).toLocaleDateString()}</span>
                <span className="font-bold">{score.scores[selectedParameter?.key]}/10</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Course Request Dialog */}
      <Dialog open={showCourseRequest} onOpenChange={setShowCourseRequest}>
        <DialogContent data-testid="course-request-dialog">
          <DialogHeader>
            <DialogTitle>Request Course Change</DialogTitle>
            <DialogDescription>Submit a request to add or remove a course</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCourseRequest} className="space-y-4">
            <div>
              <Label>Action</Label>
              <Select value={courseRequestForm.action} onValueChange={(val) => setCourseRequestForm({...courseRequestForm, action: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Course</SelectItem>
                  <SelectItem value="remove">Remove Course</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Course</Label>
              <Input 
                placeholder="Enter course name"
                value={courseRequestForm.course_id}
                onChange={(e) => setCourseRequestForm({...courseRequestForm, course_id: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label>Reason</Label>
              <Textarea 
                placeholder="Explain why you want to add/remove this course"
                value={courseRequestForm.reason}
                onChange={(e) => setCourseRequestForm({...courseRequestForm, reason: e.target.value})}
                required
              />
            </div>
            
            <Button type="submit" className="w-full">Submit Request</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Photo Dialog */}
      <Dialog open={showProfilePhoto} onOpenChange={setShowProfilePhoto}>
        <DialogContent data-testid="profile-photo-dialog">
          <DialogHeader>
            <DialogTitle>Update Profile Photo</DialogTitle>
            <DialogDescription>Upload a new profile photo</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <img 
                src={student?.profile_photo || "/placeholder-avatar.png"} 
                alt="Current Profile" 
                className="w-32 h-32 rounded-full object-cover border-4 border-pink-500"
              />
            </div>
            
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePhotoUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => photoInputRef.current?.click()}
            >
              <Camera className="w-4 h-4 mr-2" />
              Choose New Photo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCourseName} Class Schedule - Batch {student?.batch_number}</DialogTitle>
            <DialogDescription>Your date-wise class topics for {selectedCourseName}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {selectedCourseSchedule.length > 0 ? (
              selectedCourseSchedule.map((schedule, idx) => (
                <Card key={schedule.id} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 rounded-lg px-4 py-2 min-w-[80px] text-center">
                        <p className="text-xs text-blue-600 font-semibold">Day {idx + 1}</p>
                        <p className="text-sm font-bold text-blue-800">
                          {new Date(schedule.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{schedule.topic}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(schedule.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                      {schedule.attendance_marked && (
                        <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-semibold">Present</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No schedule available yet</p>
                <p className="text-sm">Your schedule will be added by the admin soon</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Submission Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Task</DialogTitle>
            <DialogDescription>
              Upload images or videos for: {selectedTask?.task_description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Course Type</Label>
              <p className="text-sm text-gray-600 capitalize">{selectedTask?.course_type}</p>
            </div>
            
            <div>
              <Label>Task Description</Label>
              <p className="text-sm text-gray-600">{selectedTask?.task_description}</p>
            </div>
            
            <div>
              <Label>Upload Files (Images/Videos)</Label>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => setTaskFiles(Array.from(e.target.files))}
                className="w-full p-2 border rounded"
              />
              {taskFiles.length > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  {taskFiles.length} file(s) selected
                </p>
              )}
            </div>
            
            <Button
              onClick={handleTaskSubmit}
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={taskFiles.length === 0}
            >
              <Upload className="w-4 h-4 mr-2" />
              Submit Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Update your account password</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="Enter current password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setPasswordDialog(false);
                  setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Practice Kit Detail Dialog */}
      <Dialog open={kitDialog} onOpenChange={setKitDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Practice Kit Details</DialogTitle>
            <DialogDescription>
              {selectedKit?.is_returned ? 'This kit has been returned' : 'This kit is currently with you'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedKit && (
            <div className="space-y-4">
              {selectedKit.kit_image_base64 && (
                <div className="w-full">
                  <img 
                    src={selectedKit.kit_image_base64} 
                    alt={selectedKit.kit_name}
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Kit Name:</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedKit.kit_name}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Issued By:</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedKit.issued_by_name}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Issue Date:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {new Date(selectedKit.issue_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    selectedKit.is_returned 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {selectedKit.is_returned ? '✓ Returned' : '📦 In Your Possession'}
                  </span>
                </div>
                
                {selectedKit.is_returned && selectedKit.return_date && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded border border-green-200">
                    <span className="text-sm font-medium text-green-700">Return Date:</span>
                    <span className="text-sm font-semibold text-green-900">
                      {new Date(selectedKit.return_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                )}
              </div>
              
              <Button
                onClick={() => setKitDialog(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
